import type { EChartsCoreOption } from "echarts/core";
import { Button } from "@/design-system/Button";
import { useEffect, useRef, useState } from "react";

import { buildPnlChartOption, type ChartPalette } from "@/shared/analytics/PnlChart";
import { projectAccountAnalytics } from "@/shared/analytics/presentation";
import { useAccountAnalytics } from "@/shared/analytics/useAccountAnalytics";
import type { SelectedPeriod } from "@/shared/period/period";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

type PreviewPreset = "today" | "7d" | "30d";

const PERIODS: Record<PreviewPreset, SelectedPeriod> = {
  today: { since: null, until: null, preset: "today" },
  "7d": { since: null, until: null, preset: "7d" },
  "30d": { since: null, until: null, preset: "30d" },
};

/** The same words the shared period control uses. Two names for one span
 *  make a reader check whether they mean the same thing, and they do. */
const LABELS: Record<PreviewPreset, string> = {
  today: "24ч",
  "7d": "7д",
  "30d": "30д",
};

type ChartHandle = {
  readonly setOption: (option: EChartsCoreOption, notMerge?: boolean) => void;
  readonly resize: () => void;
  readonly dispose: () => void;
};

function palette(): ChartPalette {
  const style = getComputedStyle(document.documentElement);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  return {
    background: token("--color-surface-default"),
    border: token("--color-border-strong"),
    grid: token("--color-chart-grid"),
    fontMono: token("--font-mono"),
    fontSans: token("--font-sans"),
    negative: token("--color-chart-negative"),
    positive: token("--color-chart-positive"),
    // Realised NET is money, so it takes the money colour rather than the
    // informational blue it used to. Green and red mean a result on every
    // other screen in this product; one chart in blue made this the exception
    // a reader has to remember.
    realised: token("--color-chart-positive"),
    text: token("--color-text-primary"),
    muted: token("--color-text-muted"),
  };
}

export function PerformancePreview(): React.JSX.Element {
  const [preset, setPreset] = useState<PreviewPreset>("7d");
  const query = useAccountAnalytics(PERIODS[preset]);
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartHandle | null>(null);
  const analytics = query.data ?? null;
  const evidence = analytics === null ? null : projectAccountAnalytics(analytics);
  const portfolioAvailable = evidence?.portfolioAvailable ?? false;

  useEffect(() => {
    const host = hostRef.current;
    if (
      host === null ||
      analytics === null ||
      evidence === null ||
      host.clientWidth === 0 ||
      host.clientHeight === 0
    ) {
      return undefined;
    }
    let cancelled = false;
    let observer: ResizeObserver | null = null;

    void import("@/shared/analytics/chartEngine").then(({ initialiseChart }) => {
      if (cancelled) return;
      const chart = initialiseChart(host);
      chartRef.current = chart;
      chart.setOption(
        buildPnlChartOption(analytics, portfolioAvailable, palette(), "both"),
        true,
      );
      observer = new ResizeObserver(() => {
        chart.resize();
      });
      observer.observe(host);
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [analytics, evidence, portfolioAvailable]);

  return (
    <section
      aria-labelledby="performance-preview-heading"
      data-testid="performance-preview"
      className="overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={`${PANEL_HEADER} flex-wrap justify-between gap-3`}>
        <h2 id="performance-preview-heading" className={PANEL_TITLE}>
          Динамика капитала
        </h2>
        <div role="group" aria-label="Период динамики" className="flex rounded-row bg-surface-muted p-0.5">
          {(Object.keys(PERIODS) as PreviewPreset[]).map((value) => (
            <Button
              key={value}
              variant="ghost"
              size="sm"
              selected={preset === value}
              aria-pressed={preset === value}
              onClick={() => {
                setPreset(value);
              }}
              className="min-h-6 px-2"
            >
              {LABELS[value]}
            </Button>
          ))}
        </div>
      </div>
      <div className="p-3">
        {query.isPending ? (
          <div className="flex h-72 items-center justify-center text-helper-sm text-text-muted">
            Читаю динамику…
          </div>
        ) : evidence === null || analytics === null ? (
          <div className="flex h-72 items-center justify-center text-helper-sm text-text-muted">
            Динамика недоступна
          </div>
        ) : (
          <div
            ref={hostRef}
            role="img"
            aria-label={`Динамика капитала за ${LABELS[preset]}`}
            className="h-72 w-full"
          />
        )}
      </div>
    </section>
  );
}
